import { useState } from 'react'
import { PlusCircleIcon } from '@heroicons/react/24/outline'
import { IoExtensionPuzzleOutline } from 'react-icons/io5'

import { ButtonApp, Container, Spinner } from '@/components'
import { SellTreez } from '@/models/sellTreez'

import { SellTreezCard, CreateAndEditSellTreezModal } from './components'
import { useCreateSellTreez, useGetAllSellTreez, useUpdateSellTreez } from './hooks'
import { FormData } from './data/types'
import { sanitizeFormData } from './utils'

export const SellTreezPage = () => {
  const { sellTreez, isLoading } = useGetAllSellTreez()
  const [isOpenModal, setIsOpenModal] = useState(false)
  const [selectedSellTreez, setSelectedSellTreez] = useState<SellTreez | undefined>(undefined)

  const handleOpenModal = () => {
    setIsOpenModal(true)
  }

  const handleCloseModal = () => {
    setIsOpenModal(false)
    setSelectedSellTreez(undefined)
  }

  const { createSellTreez, isCreating } = useCreateSellTreez(handleCloseModal)
  const { updateSellTreez, isUpdating } = useUpdateSellTreez(handleCloseModal)

  const handleSelectedSellTreez = (sellTree: SellTreez) => {
    setSelectedSellTreez(sellTree)
    setIsOpenModal(true)
  }

  const handleSubmitSellTreez = async (data: FormData) => {
    const sanitizedData = sanitizeFormData(data)
    if (selectedSellTreez) {
      updateSellTreez({ ...selectedSellTreez, ...sanitizedData })
    } else {
      createSellTreez(sanitizedData)
    }
  }

  return (
    <>
      {isLoading && <Spinner />}
      <Container
        header={
          <div className="flex xs:flex-col-reverse md:flex-row md:justify-between gap-[15px]">
            <p className="text-2xl font-bold">Sell Treez</p>
            <div className="flex justify-end gap-4">
              <ButtonApp isPrimary handleClick={handleOpenModal}>
                <div className="flex items-center gap-[10px]">
                  <PlusCircleIcon className="text-white h-[24px] w-[24px]" />
                  <span>Create new</span>
                </div>
              </ButtonApp>
            </div>
          </div>
        }
      >
        {sellTreez.length ? (
          <div className="w-full h-full grow overflow-y-auto grid grid-cols-[repeat(_auto-fill,minmax(450px,1fr)_)] 4xl:grid-cols-[repeat(_auto-fill,minmax(550px,1fr)_)] items-stretch gap-6 auto-rows-max">
            {sellTreez.map((sellTree, index) => (
              <SellTreezCard key={index} sellTreez={sellTree} onEdit={handleSelectedSellTreez} />
            ))}
          </div>
        ) : (
          !isLoading && (
            <div className="w-full h-full flex flex-col items-center justify-center py-12">
              <IoExtensionPuzzleOutline className="text-[2.5rem] text-gray-400" />
              <h3 className="mt-2 text-sm font-semibold text-gray-900">No sell treez</h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by creating a new sell treez.
              </p>
            </div>
          )
        )}
        {isOpenModal && (
          <CreateAndEditSellTreezModal
            isOpenModal={isOpenModal}
            handleCloseModal={handleCloseModal}
            sellTreez={selectedSellTreez}
            handleSubmitSellTreez={handleSubmitSellTreez}
            isLoading={isCreating || isUpdating}
          />
        )}
      </Container>
    </>
  )
}
